import { type StoredUpload, UploadError, uploadErrorFromStorage } from "@luckymenu/core";

/**
 * Sends a file to the storage service, from a browser or React Native: `POST <uploadUrl>` with
 * the upload token and the raw bytes. XMLHttpRequest, because fetch cannot
 * report upload progress. See docs/architecture/storage-service.md.
 */
export function transferFile(
	target: { uploadUrl: string; token: string },
	body: Blob,
	options: { onProgress?: (fraction: number) => void; signal?: AbortSignal } = {},
): Promise<StoredUpload> {
	return new Promise((resolve, reject) => {
		const xhr = new XMLHttpRequest();
		xhr.open("POST", target.uploadUrl);
		xhr.setRequestHeader("Authorization", `Bearer ${target.token}`);
		// Text, parsed here: React Native's XMLHttpRequest does not do "json".
		xhr.responseType = "text";
		xhr.upload.onprogress = (event) => {
			if (event.lengthComputable) options.onProgress?.(event.loaded / event.total);
		};
		xhr.onload = () => {
			let body: unknown = null;
			try {
				body = JSON.parse(xhr.responseText);
			} catch {
				// Not JSON: a proxy error page. Treated as a failure below.
			}
			if (xhr.status === 201 && body) {
				options.onProgress?.(1);
				resolve(body as StoredUpload);
				return;
			}
			const error = (body as { error?: string } | null)?.error;
			reject(new UploadError(uploadErrorFromStorage(xhr.status, error)));
		};
		xhr.onerror = () => reject(new UploadError("network"));
		xhr.onabort = () => reject(new UploadError("cancelled"));
		if (options.signal) {
			if (options.signal.aborted) {
				reject(new UploadError("cancelled"));
				return;
			}
			options.signal.addEventListener("abort", () => xhr.abort(), { once: true });
		}
		xhr.send(body);
	});
}
